<script lang="ts" setup>
import { ChartOverlay } from '@forgd/ui-kit/charts/chart-overlay'
import { ChartTitle } from '@forgd/ui-kit/charts/chart-title'
import { chartColors, withAlpha } from '@forgd/ui-kit/charts/lib/colors'
import type { ContributionPoint } from '#public-research/utils/mm-monitoring.data'
import { formatValue } from '@forgd/formatter'
import { LoadingAnimation } from '@forgd/ui-kit/blocks/animations'
import { ColorKey, ColorKeyLabel, ColorKeyValue } from '@forgd/ui-kit/color-key'
import { VisArea, VisAxis, VisLine, VisXYContainer } from '@unovis/vue'

const props = defineProps<{
  data: ContributionPoint[]
  ticks: number[]
  title: string
  yDomain?: [number, number]
  loading?: boolean
}>()

const height = 354

const series = [
  { key: 'loanCallOption', label: 'Loan + Call Option', color: chartColors.tokenDesigner[3]!, areaOpacity: 0.3 },
  { key: 'retainerWorkingCapital', label: 'Retainer + Working Capital', color: chartColors.tokenDesigner[5]!, areaOpacity: 0.1 },
] as const

const xAcc = (d: ContributionPoint) => d.index
const yAccessors = series.map(s => (d: ContributionPoint) => d[s.key])
const areaColors = series.map(s => withAlpha(s.color, s.areaOpacity))
// solid line sits on the cumulative top edge of each stacked band
const lineAccessors = series.map((_, i) => (d: ContributionPoint) =>
  series.slice(0, i + 1).reduce((sum, s) => sum + d[s.key], 0),
)

const monthByIndex = computed(() => new Map(props.data.map(d => [d.index, d.month])))

function xTickFormat(v: number) {
  return monthByIndex.value.get(v) ?? ''
}
function yTickFormat(v: number) {
  return formatValue(v, 'currency:short')
}
</script>

<template>
  <div data-el="PrMmContributionsChart" class="flex size-full flex-col">
    <ChartTitle :text="title" />
    <div v-if="loading" class="flex w-full items-center justify-center" :style="{ height: `${height}px` }">
      <LoadingAnimation />
    </div>
    <PrChartPlaceholder v-else :height="height">
      <VisXYContainer :data="data" :height="height" :y-domain="yDomain" class="w-full">
        <VisAxis
          type="y"
          :tick-format="yTickFormat"
          :num-ticks="7"
          :tick-line="false"
          :domain-line="false"
          :grid-line="false"
        />
        <VisArea :x="xAcc" :y="yAccessors" :color="areaColors" curve-type="linear" />
        <VisLine
          v-for="(s, i) in series"
          :key="s.key"
          :x="xAcc"
          :y="lineAccessors[i]"
          :color="s.color"
          curve-type="linear"
          :line-width="0.8"
        />
        <VisAxis
          type="x"
          :tick-values="ticks"
          :tick-format="xTickFormat"
          :tick-text-width="40"
          tick-text-fit-mode="wrap"
          :tick-line="false"
          :domain-line="false"
          :grid-line="false"
        />
        <ChartOverlay>
          <template #default="{ d }">
            <div class="flex min-w-55 flex-col gap-2">
              <div class="text-xs font-semibold">
                {{ d.month }}
              </div>
              <div class="flex flex-col gap-1">
                <ColorKey v-for="s in series" :key="s.key" :color="s.color" layout="justify">
                  <ColorKeyLabel>{{ s.label }}</ColorKeyLabel>
                  <ColorKeyValue>{{ formatValue(d[s.key], 'currency:short') }}</ColorKeyValue>
                </ColorKey>
              </div>
            </div>
          </template>
        </ChartOverlay>
      </VisXYContainer>
    </PrChartPlaceholder>
    <div class="mt-5 flex shrink-0 flex-wrap gap-x-5 gap-y-1">
      <ColorKey v-for="s in series" :key="s.key" :color="s.color" layout="inline" class="shrink-0">
        <ColorKeyLabel>{{ s.label }}</ColorKeyLabel>
      </ColorKey>
    </div>
  </div>
</template>
